import React, { useState, useMemo } from "react";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { StatCard, LineChart, DonutChart, CombinedBarLineChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { strainStats, strainTrends, heartRateZonesDistribution, strainRecoveryPatterns } from "../data";

export const StrainActivity: React.FC = () => {
  const theme = useTheme();
  const [trendView, setTrendView] = useState("day_strain");

  const zonesData = useMemo(() => [
    { ...heartRateZonesDistribution[0], color: "#4CAF50" },
    { ...heartRateZonesDistribution[1], color: "#8BC34A" },
    { ...heartRateZonesDistribution[2], color: "#FF9800" },
    { ...heartRateZonesDistribution[3], color: "#FF5722" },
    { ...heartRateZonesDistribution[4], color: "#F44336" },
  ], []);

  const statCards = [
    { metric: strainStats.day_strain, icon: BoltIcon },
    { metric: strainStats.active_time, icon: AccessTimeIcon },
    { metric: strainStats.calories_burn, icon: LocalFireDepartmentIcon },
  ];

  const getTrendLabel = () => {
    switch (trendView) {
      case "day_strain": return "Day Strain";
      case "cardio_load": return "Cardio Load";
      case "heart_rate": return "Heart Rate";
      case "calories_burn": return "Calories Burned";
      default: return "Day Strain";
    }
  };

  const getTrendColor = () => {
    switch (trendView) {
      case "day_strain": return theme.palette.warning.main;
      case "cardio_load": return theme.palette.info.main;
      case "heart_rate": return theme.palette.error.main;
      case "calories_burn": return theme.palette.success.main;
      default: return theme.palette.warning.main;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Strain & Activity Trends"
              subtitle="Daily strain and activity metrics over time."
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, v) => { if (v !== null) setTrendView(v); }}
                  size="small"
                >
                  <ToggleButton value="day_strain">Day Strain</ToggleButton>
                  <ToggleButton value="cardio_load">Cardio Load</ToggleButton>
                  <ToggleButton value="heart_rate">Heart Rate</ToggleButton>
                  <ToggleButton value="calories_burn">Calories</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <LineChart
                data={strainTrends}
                xAxisKey="label"
                series={[{ dataKey: trendView, label: getTrendLabel(), color: getTrendColor(), area: true }]}
                height={300}
                yAxisMin={0}
                yAxisMax={trendView === "day_strain" ? 21 : undefined}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Heart Rate Zone Distribution"
              subtitle="Time spent in each heart rate zone."
            >
              <DonutChart data={zonesData} height={300} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Strain vs Recovery by Day"
              subtitle="Weekly comparison of strain load and recovery percentage."
            >
              <CombinedBarLineChart
                data={strainRecoveryPatterns}
                xAxisKey="day"
                barSeries={[{ dataKey: "day_strain", label: "Day Strain", color: theme.palette.warning.main }]}
                lineSeries={[{ dataKey: "recovery_pct", label: "Recovery %", color: theme.palette.success.main }]}
                height={300}
                barYAxisMin={0}
                barYAxisMax={21}
                lineYAxisMin={0}
                lineYAxisMax={100}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
